import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/apiError";
import { ArrowLeft, ArrowRight, Sparkle, ChatCircleText, CheckCircle, Circle, FileArrowUp, FileText, X } from "@phosphor-icons/react";
import { useSimulatedProgress, ProgressDonut } from "@/components/ProgressDonut";

const C = { panel: "#0A2826", teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B", green: "#2CC5A8" };
const STORE_KEY = "pitch-autobuild-v2";

const DIMENSIONS = [
  { key: "what", label: "What you do", rx: null },
  { key: "customer", label: "Who pays you", rx: /customer|client|buyer|user|farmer|sme|pay|subscri|member|patient|tenant|shopper/i },
  { key: "status", label: "Where you are today", rx: /revenue|launch|pilot|mrr|arr|customer|user|kes|ksh|usd|\$|grow|pre-revenue|prototype|idea|hub|live|month/i },
  { key: "raise", label: "The raise", rx: /rais|fund|invest|debt|equity|loan|capital|grant|round|seed/i },
];

/* Status messages tied to progress bands on the donut */
const BUILD_BANDS = [
  [25, "Reading your business context…"],
  [50, "Structuring the 11-slide narrative…"],
  [80, "Writing slides from your facts…"],
  [101, "Polishing layouts & queueing questions…"],
];

const David = ({ children, sub }) => (
  <div className="flex items-start gap-3 mb-6">
    <div className="w-9 h-9 rounded-full grid place-items-center flex-shrink-0"
      style={{ backgroundColor: "rgba(10,139,133,0.25)", border: "1px solid rgba(123,211,206,0.4)" }}>
      <ChatCircleText size={18} weight="fill" style={{ color: C.tealSoft }} />
    </div>
    <div>
      <h1 className="font-extrabold text-2xl sm:text-3xl tracking-tight" style={{ letterSpacing: "-0.02em" }}>{children}</h1>
      {sub && <p className="text-white/50 mt-2 text-sm max-w-xl">{sub}</p>}
    </div>
  </div>
);

export default function PitchBuildForMe({ meta, prefill, onBack }) {
  const navigate = useNavigate();
  // phases: describe → followups → contradictions → calibrate → building
  const [phase, setPhase] = useState("describe");
  const [state, setState] = useState(() => {
    try { return { desc: "", followups: [], fuAnswers: {}, fuIdx: 0, analysis: null, docs: [], contradictions: [], cResolutions: {}, cIdx: 0, company_name: "", industry: "", funder_type: "", stage: "", ...JSON.parse(sessionStorage.getItem(STORE_KEY) || "{}") }; }
    catch { return { desc: "", followups: [], fuAnswers: {}, fuIdx: 0, analysis: null, docs: [], contradictions: [], cResolutions: {}, cIdx: 0, company_name: "", industry: "", funder_type: "", stage: "" }; }
  });
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [buildDone, setBuildDone] = useState(false);
  const simPct = useSimulatedProgress(busy);
  const buildPct = buildDone ? 100 : simPct;
  const busyMsg = buildDone ? "Deck ready — opening it now…"
    : (BUILD_BANDS.find(([until]) => buildPct < until) || BUILD_BANDS[3])[1];
  const set = (patch) => setState((s) => ({ ...s, ...patch }));

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pitch/intake/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setState((s) => ({ ...s, docs: [...s.docs, { ...data, stale: false }] }));
      toast.success(`${data.filename} read — ${data.chars.toLocaleString()} characters extracted.`);
    } catch (err) {
      if (!err.isCreditShortage) toast.error(extractErrorMessage(err, "Could not read that file"));
    } finally {
      setUploading(false);
    }
  };

  const toggleStale = async (doc) => {
    const stale = !doc.stale;
    setState((s) => ({ ...s, docs: s.docs.map((d) => d.doc_id === doc.doc_id ? { ...d, stale } : d) }));
    try { await api.post(`/pitch/intake/docs/${doc.doc_id}/stale`, { stale }); } catch { /* non-fatal */ }
  };

  useEffect(() => {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [state]);

  useEffect(() => {
    if (!prefill) return;
    setState((s) => ({ ...s, company_name: s.company_name || prefill.company_name || "" }));
  }, [prefill]);

  useEffect(() => {
    if (!prefill) return;
    setState((s) => ({ ...s, company_name: s.company_name || prefill.company_name || "" }));
  }, [prefill]);

  const words = state.desc.trim() ? state.desc.trim().split(/\s+/).length : 0;
  const meter = useMemo(() => DIMENSIONS.map((d) => ({
    ...d, lit: d.key === "what" ? words >= 30 : d.rx.test(state.desc),
  })), [state.desc, words]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post("/pitch/intake/analyze",
        { description: state.desc, document_ids: state.docs.map((d) => d.doc_id) });
      const fu = data.followups || [];
      const contra = data.contradictions || [];
      set({
        analysis: data, followups: fu, fuIdx: 0, contradictions: contra, cIdx: 0,
        stage: state.stage || data.stage_guess || "",
        industry: state.industry || (data.industry_guesses || [])[0] || "",
      });
      setPhase(fu.length ? "followups" : contra.length ? "contradictions" : "calibrate");
    } catch (err) {
      if (!err.isCreditShortage) toast.error(extractErrorMessage(err, "Could not analyse your description"));
    } finally {
      setAnalyzing(false);
    }
  };

  const afterFollowups = () => setPhase(state.contradictions.length ? "contradictions" : "calibrate");

  const build = async () => {
    setBusy(true);
    setPhase("building");
    try {
      const contradictionAnswers = Object.fromEntries(
        Object.entries(state.cResolutions).map(([topic, v]) => [`Resolved conflict — ${topic}`, v]));
      const { data } = await api.post("/pitch/decks/build-for-me", {
        company_name: state.company_name,
        description: state.desc,
        followup_answers: {
          ...Object.fromEntries(
            state.followups.map((f) => [f.dimension || f.question, state.fuAnswers[f.question] || ""])),
          ...contradictionAnswers,
        },
        industry: state.industry || "other",
        industry_other_desc: state.industry === "other" ? (state.industry_other_desc || "") : "",
        funder_type: state.funder_type || "equity",
        stage: state.stage || "",
        country: prefill?.country || "",
        document_ids: state.docs.map((d) => d.doc_id),
      });
      sessionStorage.removeItem(STORE_KEY);
      // Snap the donut to 100% and let the founder see it land before we route.
      setBuildDone(true);
      await new Promise((r) => setTimeout(r, 800));
      if (data.drafted) {
        const nq = (data.facts_queue || []).length;
        toast.success(`Draft ready — ${data.filled.length} slides written from your input.${nq ? ` ${nq} facts need you.` : ""}`, { duration: 9000 });
      } else {
        toast.error(data.message || "Drafting failed — credits refunded.");
      }
      navigate(`/pitch/${data.deck_id}`);
    } catch (err) {
      if (!err.isCreditShortage) toast.error(extractErrorMessage(err, "Could not build your deck"));
      setBusy(false);
      setPhase("calibrate");
    }
  };

  if (phase === "building") {
    return (
      <div className="py-20 text-center" data-testid="forme-building">
        <ProgressDonut pct={buildPct} size={150} stroke={12} testid="forme-build-donut" />
        <h2 className="text-2xl font-extrabold mt-6">David is building your deck.</h2>
        <p className="text-white/50 mt-2 text-sm" data-testid="forme-busy-msg">{busyMsg}</p>
        <p className="text-white/30 mt-6 text-xs">He assumes narrative, never evidence — missing hard facts become questions for you.</p>
      </div>
    );
  }

  if (phase === "describe") {
    return (
      <div data-testid="forme-interview">
        <button onClick={onBack} data-testid="forme-back-btn"
          className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors mb-8">
          <ArrowLeft size={14} /> Change how you build
        </button>
        <David sub="In your own words — no structure needed. The more real detail (numbers, names, prices), the more of your deck gets written from facts instead of questions.">
          Tell me about your business — what you do, who pays you, and where you are today.
        </David>
        <textarea autoFocus rows={10} value={state.desc} onChange={(e) => set({ desc: e.target.value })}
          data-testid="forme-description"
          placeholder="e.g. We run solar-powered milk chilling hubs for smallholder dairy farmers in Kenya. Farmers pay KES 3 per litre chilled via M-Pesa. 12 hubs are live serving 2,400 farmers, KES 1.2M monthly revenue growing 18% month on month. We're raising KES 45M in debt to build 30 more hubs…"
          className="w-full rounded-md px-4 py-3 text-sm text-white bg-white/[0.06] border border-white/10 focus:border-[#0A8B85] focus:outline-none leading-relaxed" />
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="forme-sufficiency-meter">
          {meter.map((d) => (
            <span key={d.key} className="inline-flex items-center gap-1.5 text-[12px]" data-testid={`forme-dim-${d.key}`}>
              {d.lit ? <CheckCircle size={15} weight="fill" style={{ color: C.green }} />
                     : <Circle size={15} style={{ color: "rgba(255,255,255,0.25)" }} />}
              <span style={{ color: d.lit ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.35)" }}>{d.label}</span>
            </span>
          ))}
          <span className="text-[11px] text-white/35 ml-auto" data-testid="forme-wordcount">
            {words} words{words < 150 ? ` · aim for 150+` : " ✓"}
          </span>
        </div>
        <div className="mt-6 rounded-lg border border-dashed px-4 py-4"
          style={{ borderColor: "rgba(123,211,206,0.3)", backgroundColor: "rgba(10,139,133,0.05)" }}
          data-testid="forme-upload-zone">
          <div className="flex flex-wrap items-center gap-3">
            <FileArrowUp size={20} style={{ color: C.tealSoft }} />
            <div className="text-[13px] text-white/70 flex-1 min-w-[220px]">
              <span className="font-bold text-white/90">Have documents?</span>{" "}
              Drop in a business plan, old deck or financials — David extracts the facts.
              <span className="text-white/40"> PDF · DOCX · PPTX · XLSX · max 15MB</span>
            </div>
            <label className="cursor-pointer rounded-md px-4 py-2 text-xs font-bold uppercase tracking-wider"
              style={{ border: "1px solid rgba(123,211,206,0.5)", color: C.tealSoft }}>
              {uploading ? "Reading…" : "Upload"}
              <input type="file" className="hidden" accept=".pdf,.docx,.pptx,.xlsx,.txt"
                data-testid="forme-upload-input" disabled={uploading}
                onChange={(e) => { uploadDoc(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          </div>
          {state.docs.length > 0 && (
            <div className="mt-3 space-y-2">
              {state.docs.map((d) => (
                <div key={d.doc_id} className="flex flex-wrap items-center gap-2 text-[12px]" data-testid={`forme-doc-${d.doc_id}`}>
                  <FileText size={15} style={{ color: C.tealSoft }} />
                  <span className="text-white/85 font-semibold">{d.filename}</span>
                  <span className="text-white/35">{(d.chars || 0).toLocaleString()} chars</span>
                  <label className="inline-flex items-center gap-1.5 text-white/50 cursor-pointer ml-2">
                    <input type="checkbox" checked={!!d.stale} onChange={() => toggleStale(d)}
                      className="accent-[#F59E0B]" data-testid={`forme-doc-stale-${d.doc_id}`} />
                    over 12 months old
                  </label>
                  <button onClick={() => setState((s) => ({ ...s, docs: s.docs.filter((x) => x.doc_id !== d.doc_id) }))}
                    className="text-white/30 hover:text-white/70" data-testid={`forme-doc-remove-${d.doc_id}`}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-8 flex justify-end">
          <button onClick={analyze} disabled={(words < 40 && state.docs.length === 0) || analyzing} data-testid="forme-analyze-btn"
            className="inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-bold disabled:opacity-40"
            style={{ backgroundColor: C.teal, color: "#fff" }}>
            {analyzing ? "David is reading…" : "Continue"} <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </div>
    );
  }

  if (phase === "followups") {
    const fu = state.followups[state.fuIdx];
    const val = state.fuAnswers[fu.question] || "";
    const last = state.fuIdx === state.followups.length - 1;
    return (
      <div data-testid="forme-followups">
        <button onClick={() => (state.fuIdx === 0 ? setPhase("describe") : set({ fuIdx: state.fuIdx - 1 }))}
          data-testid="forme-back-btn"
          className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors mb-8">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="text-[11px] uppercase tracking-[0.25em] font-bold mb-4" style={{ color: C.tealSoft }}>
          A few gaps to close · {state.fuIdx + 1} of {state.followups.length}
        </div>
        <David sub="If you don't know yet, say so — David will queue it as a fact for later instead of guessing.">
          {fu.question}
        </David>
        <textarea autoFocus rows={4} value={val}
          onChange={(e) => set({ fuAnswers: { ...state.fuAnswers, [fu.question]: e.target.value } })}
          data-testid="forme-fu-input"
          className="w-full rounded-md px-4 py-3 text-sm text-white bg-white/[0.06] border border-white/10 focus:border-[#0A8B85] focus:outline-none leading-relaxed" />
        <div className="mt-8 flex justify-end">
          <button onClick={() => (last ? afterFollowups() : set({ fuIdx: state.fuIdx + 1 }))}
            data-testid="forme-fu-next-btn"
            className="inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-bold"
            style={{ backgroundColor: C.teal, color: "#fff" }}>
            {val.trim() ? "Next" : "I don't know yet"} <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </div>
    );
  }

  if (phase === "contradictions") {
    const c = state.contradictions[state.cIdx];
    const chosen = state.cResolutions[c.topic] || "";
    const lastC = state.cIdx === state.contradictions.length - 1;
    const pick = (v) => set({ cResolutions: { ...state.cResolutions, [c.topic]: v } });
    return (
      <div data-testid="forme-contradictions">
        <button onClick={() => (state.cIdx === 0
            ? setPhase(state.followups.length ? "followups" : "describe")
            : set({ cIdx: state.cIdx - 1 }))}
          data-testid="forme-back-btn"
          className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors mb-8">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="text-[11px] uppercase tracking-[0.25em] font-bold mb-4" style={{ color: C.orange }}>
          Your sources disagree · {state.cIdx + 1} of {state.contradictions.length}
        </div>
        <David sub="David never resolves a conflict silently — you decide which number goes in front of funders.">
          {c.question || `${c.topic}: which is current?`}
        </David>
        <div className="flex flex-col gap-2 max-w-xl" data-testid="forme-contra-options">
          {[c.typed_value && ["typed", `What you typed: ${c.typed_value}`],
            c.document_value && ["doc", `Your document: ${c.document_value}`]]
            .filter(Boolean).map(([k, label]) => (
            <button key={k} onClick={() => pick(label)} data-testid={`forme-contra-${k}`}
              className="text-left rounded-md px-4 py-3 text-sm transition-colors"
              style={{ backgroundColor: chosen === label ? "rgba(10,139,133,0.25)" : "rgba(255,255,255,0.04)",
                       border: `1px solid ${chosen === label ? C.teal : "rgba(255,255,255,0.12)"}`,
                       color: "rgba(255,255,255,0.85)" }}>
              {label}
            </button>
          ))}
          <input placeholder="Neither — the current figure is…"
            value={chosen.startsWith("Corrected: ") ? chosen.slice(11) : ""}
            onChange={(e) => pick(e.target.value ? `Corrected: ${e.target.value}` : "")}
            data-testid="forme-contra-custom"
            className="rounded-md px-4 py-3 text-sm text-white bg-white/[0.06] border border-white/10 focus:border-[#0A8B85] focus:outline-none" />
        </div>
        <div className="mt-8 flex justify-end">
          <button onClick={() => (lastC ? setPhase("calibrate") : set({ cIdx: state.cIdx + 1 }))}
            disabled={!chosen} data-testid="forme-contra-next-btn"
            className="inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-bold disabled:opacity-40"
            style={{ backgroundColor: C.teal, color: "#fff" }}>
            Next <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </div>
    );
  }

  // phase === "calibrate"
  const stageGuess = state.analysis?.stage_guess;
  const indGuesses = state.analysis?.industry_guesses || [];
  const indOptions = (meta?.industries || []).filter((i) =>
    indGuesses.includes(i.id) || indGuesses.length === 0);
  const otherInds = (meta?.industries || []).filter((i) => !indGuesses.includes(i.id));
  return (
    <div data-testid="forme-calibrate">
      <button onClick={() => setPhase(state.contradictions.length ? "contradictions" : state.followups.length ? "followups" : "describe")} data-testid="forme-back-btn"
        className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors mb-8">
        <ArrowLeft size={14} /> Back
      </button>
      <David sub={state.analysis?.stage_reason ? `Why: ${state.analysis.stage_reason}` : undefined}>
        Last checks — David inferred these, you confirm them.
      </David>

      <div className="space-y-7 max-w-2xl">
        <div>
          <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-2">Company name <span style={{ color: C.orange }}>*</span></div>
          <input value={state.company_name} onChange={(e) => set({ company_name: e.target.value })}
            data-testid="forme-company-input"
            className="w-full rounded-md px-4 py-2.5 text-sm text-white bg-white/[0.06] border border-white/10 focus:border-[#0A8B85] focus:outline-none" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-2">
            Stage {stageGuess ? <span className="normal-case text-white/35 font-normal">— David's guess: {stageGuess}. Correct?</span> : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {["Pre-revenue", "Early revenue", "Growth"].map((s) => (
              <button key={s} onClick={() => set({ stage: s })} data-testid={`forme-stage-${s.toLowerCase().replace(" ", "-")}`}
                className="rounded-full px-4 py-2 text-sm font-semibold transition-colors"
                style={{ backgroundColor: state.stage === s ? C.teal : "rgba(255,255,255,0.06)",
                         color: state.stage === s ? "#fff" : "rgba(255,255,255,0.65)",
                         border: `1px solid ${state.stage === s ? C.teal : "rgba(255,255,255,0.12)"}` }}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-2">
            Industry {indGuesses.length ? <span className="normal-case text-white/35 font-normal">— David's best match{indGuesses.length > 1 ? "es" : ""} first</span> : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {[...indOptions, ...otherInds].map((i) => (
              <button key={i.id} onClick={() => set({ industry: i.id })} data-testid={`forme-ind-${i.id}`}
                className="rounded-full px-4 py-2 text-sm font-semibold transition-colors"
                style={{ backgroundColor: state.industry === i.id ? C.teal : "rgba(255,255,255,0.06)",
                         color: state.industry === i.id ? "#fff" : indGuesses.includes(i.id) ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.5)",
                         border: `1px solid ${state.industry === i.id ? C.teal : indGuesses.includes(i.id) ? "rgba(123,211,206,0.4)" : "rgba(255,255,255,0.12)"}` }}>
                {i.name}{indGuesses.includes(i.id) && state.industry !== i.id ? " ✦" : ""}
              </button>
            ))}
          </div>
          {state.industry === "other" && (
            <div className="mt-3 max-w-md">
              <input type="text" value={state.industry_other_desc || ""}
                onChange={(e) => set({ industry_other_desc: e.target.value })}
                data-testid="forme-industry-other-desc"
                placeholder="Briefly describe your industry — e.g. Waste recycling marketplace"
                className="w-full px-3 py-2.5 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85]"
                style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
              <div className="text-[11px] text-white/40 mt-1.5">
                We&apos;ll apply the general template — tell David anything industry-specific in your description above.
              </div>
            </div>
          )}
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-2">
            Who are you raising from? <span style={{ color: C.orange }}>*</span>
            <span className="normal-case text-white/35 font-normal"> — never inferred, your call</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {(meta?.funder_types || []).map((f) => (
              <button key={f.id} onClick={() => set({ funder_type: f.id })} data-testid={`forme-funder-${f.id}`}
                className="rounded-full px-4 py-2 text-sm font-semibold transition-colors"
                style={{ backgroundColor: state.funder_type === f.id ? C.teal : "rgba(255,255,255,0.06)",
                         color: state.funder_type === f.id ? "#fff" : "rgba(255,255,255,0.65)",
                         border: `1px solid ${state.funder_type === f.id ? C.teal : "rgba(255,255,255,0.12)"}` }}>
                {f.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-10 flex items-center justify-between">
        <div className="text-[11px] text-white/35 max-w-xs">
          David drafts all slides from your material — 25 credits. Missing hard facts become questions, never guesses.
        </div>
        <button onClick={build}
          disabled={!state.company_name.trim() || !state.funder_type || !state.stage || !state.industry}
          data-testid="forme-build-btn"
          className="inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-40"
          style={{ backgroundColor: C.orange, color: "#000" }}>
          <Sparkle size={15} weight="fill" /> Build my deck · 25 credits
        </button>
      </div>
    </div>
  );
}
